import { buildExcelColumns, setExportExcelDefaultColWidths, setExportExcelDefaultStyle } from '@/pages/dashboard/utils/utils';
import { FILE_TYPES_MAP } from '@gp/constants/const';
import { downloadFile } from '@intelligence/utils/commonUtils';
import moment from 'moment';

/**
 * 导出数据为Excel或CSV格式
 * @param {Object} params 参数对象
 * @param {string} params.fileName 文件名
 * @param {Array} params.columns 列配置数组
 * @param {Array} params.data 数据数组
 * @param {string} params.fileType 文件类型(FILE_TYPES_MAP.EXCEL或FILE_TYPES_MAP.CSV)
 */
export function exportData({ fileName, columns, data, fileType }) {
  if (fileType === FILE_TYPES_MAP.CSV) {
    exportToCSV({ fileName, columns, data });
  } else {
    exportToExcel({ fileName, columns, data });
  }
}

/**
 * 导出数据为CSV格式
 * @param {Object} params 参数对象
 * @param {string} params.fileName 文件名
 * @param {Array} params.columns 列配置数组
 * @param {Array} params.data 数据数组
 */
function exportToCSV({ fileName, columns, data }) {
  const headers = columns?.map((item) => item.header) || [];
  const keys = columns?.map((item) => item.key) || [];

  // 创建CSV内容
  let csvContent = headers.join(',') + '\n';

  data?.forEach((item) => {
    const row = keys.map((key) => {
      const value = item[key];
      // 处理空值
      if (value === null || value === undefined) {
        return '';
      }
      // 处理包含逗号的值
      if (typeof value === 'string' && value.includes(',')) {
        return `"${value}"`;
      }
      return value;
    });
    csvContent += row.join(',') + '\n';
  });

  // 创建下载链接
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  downloadFile({url,  fileName: `${fileName}.csv`});
}

/**
 * 导出数据为Excel格式
 * @param {Object} params 参数对象
 * @param {string} params.fileName 文件名
 * @param {Array} params.columns 列配置数组
 * @param {Array} params.data 数据数组
 */
function exportToExcel({ fileName, columns, data }) {
  const excelColumn = buildExcelColumns(columns?.length ?? 0);
  const headerRow = {};
  const header: string[] = [];
  const keys: string[] = [];

  columns?.forEach((item) => {
    header.push(item.header);
    keys.push(item.key);
  });
  header?.forEach((item, index) => {
    headerRow[excelColumn[index]] = item;
  });
  const excelData = [headerRow];

  data?.forEach((item) => {
    const itemData = {};
    keys?.forEach((key, index) => {
      const value = item[key];
      // 处理数值类型
      if (typeof value === 'number') {
        itemData[excelColumn[index]] = {
          v: value,
          t: 'n',
          z: value % 1 === 0 ? '#,##0;-#,##0' : '##,##0.00 ;-##,##0.00',
        };
      }
      // 处理百分比字段
      else if (key.includes('_percent') && typeof value === 'number') {
        itemData[excelColumn[index]] = {
          v: value,
          t: 'n',
          z: '##,##0.00%" ";-##,##0.00%',
        };
      }
      // 处理空值
      else if (value === null || value === undefined) {
        itemData[excelColumn[index]] = '-';
      }
      // 其他情况
      else {
        itemData[excelColumn[index]] = value;
      }
    });
    excelData.push(itemData);
  });

  // 导出excel
  exportExcel(fileName, excelData);
}

/**
 * 导出Excel文件
 * @param {string} fileName
 * @param {object[]} table 原始数值
 */

const exportExcel = async (fileName: string, table: never[] | undefined, colWidth = []) => {
  fileName = fileName?.replace(/\u0020/g, '_');

  setExportExcelDefaultStyle(table);
  // 创建book
  const XLSX = await import('xlsx-js-style');
  const wb = XLSX.utils.book_new();
  // json转sheet
  const ws = XLSX.utils.json_to_sheet(table || [], { header: [], skipHeader: true });
  if (colWidth && colWidth.length > 0) {
    ws['!cols'] = colWidth;
  } else {
    setExportExcelDefaultColWidths(ws, table);
  }
  // 下载时操作平台不允许输入半角冒号，所以用全角的代替
  const timestamp = moment().format('YYYY_MM_DD HH_mm_ss');
  // sheet写入book
  XLSX.utils.book_append_sheet(wb, ws, "file");
  // 输出
  XLSX.writeFile(wb, fileName ? `${fileName}.xlsx` : `${timestamp}.xlsx`);
};
